import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FlaskConical, Loader2, RefreshCw, Download, Square, Play, ShieldAlert } from 'lucide-react';
import { adminApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import BackButton from '../../components/BackButton';

const usd = (v) => (typeof v === 'number' ? `${v.toFixed(4)} $` : '—');
const RESUMABLE = ['interrupted', 'stopped_budget', 'cancelled'];
const parseIds = (s) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);

const Stat = ({ label, value, testid }) => (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3">
        <div className="text-xs text-[#6B7280]">{label}</div>
        <div className="text-lg font-semibold text-[#0A0A0A]" data-testid={testid}>{value}</div>
    </div>
);

const EstimateForm = ({ orgs, onEstimate, busy }) => {
    const { t } = useTranslation();
    const [orgId, setOrgId] = useState('');
    const [jobIds, setJobIds] = useState('');
    const [candIds, setCandIds] = useState('');
    const [maxPairs, setMaxPairs] = useState(50);
    const submit = () => onEstimate({
        organization_id: orgId,
        job_ids: parseIds(jobIds).length ? parseIds(jobIds) : null,
        candidate_ids: parseIds(candIds).length ? parseIds(candIds) : null,
        max_pairs: Number(maxPairs) || 1,
    });
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-4" data-testid="cmp-estimate-form">
            <h2 className="font-semibold">{t('scoring_compare.step1')}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
                <label className="text-sm space-y-1">
                    <span>{t('scoring_compare.organization')}</span>
                    <select className="w-full border border-[#E5E7EB] rounded-md h-10 px-2 bg-white" value={orgId} onChange={(e) => setOrgId(e.target.value)} data-testid="cmp-org-select">
                        <option value="">{t('scoring_compare.choose_org')}</option>
                        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name} ({o.scoring_engine_v2 ? 'V2' : 'V1'})</option>)}
                    </select>
                </label>
                <label className="text-sm space-y-1">
                    <span>{t('scoring_compare.max_pairs')}</span>
                    <Input type="number" min={1} max={500} value={maxPairs} onChange={(e) => setMaxPairs(e.target.value)} data-testid="cmp-max-pairs" />
                </label>
                <label className="text-sm space-y-1">
                    <span>{t('scoring_compare.job_ids')}</span>
                    <Input value={jobIds} onChange={(e) => setJobIds(e.target.value)} placeholder={t('scoring_compare.optional_ids')} data-testid="cmp-job-ids" />
                </label>
                <label className="text-sm space-y-1">
                    <span>{t('scoring_compare.candidate_ids')}</span>
                    <Input value={candIds} onChange={(e) => setCandIds(e.target.value)} placeholder={t('scoring_compare.optional_ids')} data-testid="cmp-candidate-ids" />
                </label>
            </div>
            <Button onClick={submit} disabled={!orgId || busy} className="bg-[#002FA7] hover:bg-[#002480] text-white" data-testid="cmp-estimate-btn">
                {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{t('scoring_compare.estimate_btn')}
            </Button>
        </div>
    );
};

const EstimateCard = ({ est, onLaunch, busy }) => {
    const { t } = useTranslation();
    const [budget, setBudget] = useState(String(est.recommended_max_cost_usd || ''));
    const [confirm, setConfirm] = useState(false);
    const fallback = Object.values(est.cost_model?.fallback_used || {}).some(Boolean);
    return (
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-4" data-testid="cmp-estimate-card">
            <h2 className="font-semibold">{t('scoring_compare.step2')}</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label={t('scoring_compare.pairs')} value={`${est.pairs} / ${est.eligible_pairs}`} testid="cmp-est-pairs" />
                <Stat label={t('scoring_compare.calls')} value={`${est.llm_calls_min}–${est.llm_calls_max} (~${est.llm_calls_expected})`} testid="cmp-est-calls" />
                <Stat label={t('scoring_compare.cost_expected')} value={usd(est.cost_expected_usd)} testid="cmp-est-cost" />
                <Stat label={t('scoring_compare.cost_worst')} value={usd(est.cost_worst_usd)} testid="cmp-est-worst" />
            </div>
            {est.requested_candidates_out_of_scope > 0 && (
                <p className="text-sm text-[#B45309]" data-testid="cmp-est-out-of-scope">{t('scoring_compare.out_of_scope', { count: est.requested_candidates_out_of_scope })}</p>
            )}
            {fallback && <p className="text-xs text-[#6B7280]">{t('scoring_compare.fallback_note')}</p>}
            <p className="text-xs text-[#6B7280]">{t('scoring_compare.reservation_note', { amount: usd(est.budget_reservation_per_pair_usd) })}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
                <label className="text-sm space-y-1">
                    <span>{t('scoring_compare.budget')}</span>
                    <Input type="number" step="0.01" min="0.01" max="50" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="cmp-budget" />
                </label>
                <Button onClick={() => setConfirm(true)} disabled={busy || !est.pairs || !(Number(budget) > 0)} className="bg-[#D97706] hover:bg-[#B45309] text-white" data-testid="cmp-launch-btn">
                    <Play className="w-4 h-4 mr-2" />{t('scoring_compare.launch')}
                </Button>
            </div>
            <AlertDialog open={confirm} onOpenChange={setConfirm}>
                <AlertDialogContent data-testid="cmp-launch-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('scoring_compare.confirm_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('scoring_compare.confirm_body', { pairs: est.pairs, budget: Number(budget).toFixed(2) })}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cmp-launch-cancel">{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => onLaunch(Number(budget))} data-testid="cmp-launch-confirm-btn">{t('scoring_compare.launch')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

const ResultsTable = ({ results }) => {
    const { t } = useTranslation();
    const pairs = {};
    results.forEach((r) => { (pairs[`${r.candidate_id}|${r.job_id}`] ||= {})[r.engine] = r; });
    const cell = (r) => (r?.status === 'ok' ? r.scores?.overall : <span className="text-[#EF4444]" title={r?.error}>{t('scoring_compare.error')}</span>);
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cmp-results-table">
                <thead><tr className="text-left text-[#6B7280] border-b">
                    <th className="py-2 pr-3">{t('scoring_compare.candidate')}</th><th className="pr-3">V1</th><th className="pr-3">V2</th>
                    <th className="pr-3">Δ</th><th className="pr-3">{t('scoring_compare.v2_model')}</th><th className="pr-3">{t('scoring_compare.current_score')}</th>
                </tr></thead>
                <tbody>
                    {Object.entries(pairs).map(([key, e]) => {
                        const d = e.v1?.status === 'ok' && e.v2?.status === 'ok' ? e.v2.scores.overall - e.v1.scores.overall : null;
                        const any = e.v1 || e.v2;
                        return (
                            <tr key={key} className="border-b last:border-0" data-testid={`cmp-row-${any.candidate_id}`}>
                                <td className="py-2 pr-3 font-mono text-xs">{any.candidate_id.slice(0, 12)}</td>
                                <td className="pr-3">{cell(e.v1)}</td><td className="pr-3">{cell(e.v2)}</td>
                                <td className="pr-3 font-medium">{d === null ? '—' : (d > 0 ? `+${d}` : d)}</td>
                                <td className="pr-3 text-xs">{e.v2?.model_used || '—'}{e.v2?.escalated ? ' ↑' : ''}</td>
                                <td className="pr-3 text-xs">{any.existing_score?.overall ?? '—'} {any.existing_score?.engine_version ? `(${any.existing_score.engine_version})` : ''}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const RunRow = ({ run, orgName, onSelect, onCancel, onResume, onCsv, selected }) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language === 'en' ? 'en-GB' : 'fr-FR';
    return (
        <tr className={`border-b last:border-0 ${selected ? 'bg-[#EFF6FF]' : ''}`} data-testid={`cmp-run-${run.id}`}>
            <td className="py-2 pr-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#F3F4F6]" data-testid={`cmp-run-status-${run.id}`}>{t(`scoring_compare.status_${run.status}`, run.status)}</span></td>
            <td className="pr-3">{orgName}</td>
            <td className="pr-3">{run.pairs_done}/{run.pairs_total}{run.pairs_error ? ` · ${run.pairs_error} ${t('scoring_compare.errors')}` : ''}</td>
            <td className="pr-3">{usd(run.spent_usd)} / {Number(run.max_cost_usd).toFixed(2)} $</td>
            <td className="pr-3 text-xs">{run.created_at ? new Date(run.created_at).toLocaleString(locale) : '—'}</td>
            <td className="flex flex-wrap gap-1 py-2">
                <Button size="sm" variant="outline" onClick={() => onSelect(run)} data-testid={`cmp-run-view-${run.id}`}>{t('scoring_compare.view')}</Button>
                <Button size="sm" variant="outline" onClick={() => onCsv(run)} title={t('scoring_compare.csv')} aria-label={t('scoring_compare.csv')} data-testid={`cmp-run-csv-${run.id}`}><Download className="w-3 h-3" /></Button>
                {run.status === 'running' && <Button size="sm" variant="outline" onClick={() => onCancel(run)} title={t('scoring_compare.cancel_run')} aria-label={t('scoring_compare.cancel_run')} data-testid={`cmp-run-cancel-${run.id}`}><Square className="w-3 h-3" /></Button>}
                {RESUMABLE.includes(run.status) && <Button size="sm" variant="outline" onClick={() => onResume(run)} title={t('scoring_compare.resume')} aria-label={t('scoring_compare.resume')} data-testid={`cmp-run-resume-${run.id}`}><Play className="w-3 h-3" /></Button>}
            </td>
        </tr>
    );
};

const ResumeDialog = ({ run, onClose, onConfirm }) => {
    const { t } = useTranslation();
    const [budget, setBudget] = useState(String(run ? run.max_cost_usd : ''));
    return (
        <AlertDialog open={!!run} onOpenChange={(o) => { if (!o) onClose(); }}>
            <AlertDialogContent data-testid="cmp-resume-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('scoring_compare.resume_title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('scoring_compare.resume_body', { spent: usd(run?.spent_usd) })}</AlertDialogDescription>
                </AlertDialogHeader>
                <Input type="number" step="0.01" min="0.01" max="50" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="cmp-resume-budget" />
                <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction disabled={!(Number(budget) > 0 && Number(budget) <= 50)} onClick={() => onConfirm(Number(budget))} data-testid="cmp-resume-confirm-btn">{t('scoring_compare.resume')}</AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default function AdminScoringComparePage() {
    const { t } = useTranslation();
    const [orgs, setOrgs] = useState([]);
    const [est, setEst] = useState(null);
    const [busy, setBusy] = useState(false);
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [results, setResults] = useState([]);
    const [resumeRun, setResumeRun] = useState(null);
    const [cancelRun, setCancelRun] = useState(null);

    const loadRuns = useCallback(async () => {
        try { setRuns((await adminApi.scoringCompareRuns()).data.runs || []); } catch (e) { toast.error(formatApiError(e)); }
    }, []);
    const loadResults = useCallback(async (run) => {
        try { setResults((await adminApi.scoringCompareResults(run.id)).data.results || []); } catch (e) { toast.error(formatApiError(e)); }
    }, []);

    useEffect(() => {
        adminApi.getScoringEngineStatus().then((r) => setOrgs(r.data.orgs || [])).catch((e) => toast.error(formatApiError(e)));
        loadRuns();
    }, [loadRuns]);

    const anyRunning = runs.some((r) => r.status === 'running');
    useEffect(() => {
        if (!anyRunning) return undefined;
        const id = setInterval(() => { loadRuns(); if (selected) loadResults(selected); }, 4000);
        return () => clearInterval(id);
    }, [anyRunning, selected, loadRuns, loadResults]);

    const estimate = async (body) => {
        setBusy(true);
        try { setEst((await adminApi.scoringCompareEstimate(body)).data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const launch = async (budget) => {
        setBusy(true);
        try {
            const run = (await adminApi.scoringCompareStart({ estimate_token: est.estimate_token, max_cost_usd: budget })).data;
            setEst(null); setSelected(run); setResults([]);
            toast.success(t('scoring_compare.started'));
            loadRuns();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const act = (fn) => async (run) => { try { await fn(run); loadRuns(); } catch (e) { toast.error(formatApiError(e)); } };
    const downloadCsv = async (run) => {
        try {
            const res = await adminApi.scoringCompareCsv(run.id);
            const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
            const a = document.createElement('a');
            a.href = url; a.download = `scoring-comparison-${run.id}.csv`; a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const orgName = (id) => orgs.find((o) => o.id === id)?.name || id;

    return (
        <div className="space-y-6 pb-20" data-testid="scoring-compare-page">
            <BackButton />
            <div className="flex items-center gap-3">
                <FlaskConical className="w-6 h-6 text-[#002FA7]" />
                <h1 className="text-2xl font-bold font-['Outfit']">{t('scoring_compare.title')}</h1>
            </div>
            <div className="flex gap-2 text-sm bg-[#FFFBEB] border border-[#FDE68A] text-[#92400E] rounded-lg p-3" data-testid="cmp-warning">
                <ShieldAlert className="w-5 h-5 flex-shrink-0" /><span>{t('scoring_compare.warning')}</span>
            </div>
            <EstimateForm orgs={orgs} onEstimate={estimate} busy={busy} />
            {est && <EstimateCard key={est.estimate_token} est={est} onLaunch={launch} busy={busy} />}
            <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="font-semibold">{t('scoring_compare.runs')}</h2>
                    <Button size="sm" variant="outline" onClick={loadRuns} data-testid="cmp-runs-refresh"><RefreshCw className="w-4 h-4" /></Button>
                </div>
                {runs.length === 0 ? <p className="text-sm text-[#6B7280]">{t('scoring_compare.no_runs')}</p> : (
                    <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="cmp-runs-table">
                        <thead><tr className="text-left text-[#6B7280] border-b">
                            <th className="py-2 pr-3">{t('scoring_compare.col_status')}</th><th className="pr-3">{t('scoring_compare.organization')}</th>
                            <th className="pr-3">{t('scoring_compare.col_progress')}</th><th className="pr-3">{t('scoring_compare.col_cost')}</th>
                            <th className="pr-3">{t('scoring_compare.col_date')}</th><th>{t('scoring_compare.col_actions')}</th>
                        </tr></thead><tbody>
                        {runs.map((r) => (
                            <RunRow key={r.id} run={r} orgName={orgName(r.organization_id)} selected={selected?.id === r.id}
                                onSelect={(run) => { setSelected(run); loadResults(run); }}
                                onCancel={setCancelRun}
                                onResume={setResumeRun}
                                onCsv={downloadCsv} />
                        ))}
                    </tbody></table></div>
                )}
            </div>
            <AlertDialog open={!!cancelRun} onOpenChange={(o) => { if (!o) setCancelRun(null); }}>
                <AlertDialogContent data-testid="cmp-cancel-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('scoring_compare.cancel_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('scoring_compare.cancel_body')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.back')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => { const run = cancelRun; setCancelRun(null); act((r) => adminApi.scoringCompareCancel(r.id))(run); }} data-testid="cmp-cancel-confirm-btn">{t('scoring_compare.cancel_run')}</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
            <ResumeDialog key={resumeRun?.id || 'none'} run={resumeRun} onClose={() => setResumeRun(null)}
                onConfirm={(budget) => { const run = resumeRun; setResumeRun(null); act((r) => adminApi.scoringCompareResume(r.id, budget))(run); }} />
            {selected && (
                <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-3" data-testid="cmp-results">
                    <h2 className="font-semibold">{t('scoring_compare.results')}</h2>
                    {results.length === 0 ? <p className="text-sm text-[#6B7280]">{t('scoring_compare.no_results')}</p> : <ResultsTable results={results} />}
                </div>
            )}
        </div>
    );
}
